import { Link } from "@/lib/router-compat";
import { Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Carousel, CarouselContent, CarouselItem, CarouselApi } from "@/components/ui/carousel";
import { useState, useEffect } from "react";

const packages = [
  {
    name: "Content-Kickstart",
    description: "Perfekt für Unternehmen, die mit professionellem Content und klarer Strategie online konsistent bleiben möchten.",
    features: [
      "1x Online-Strategiesitzung pro Monat zur Planung der kommenden Inhalte",
      "1x Vor-Ort-Shooting pro Monat (4 Stunden) mit filmischer Qualität",
      "4 professionelle Videos pro Monat (1 pro Woche)",
      "1 kostenlose Überarbeitung pro fertigem Video",
      "Premium-Schnitt mit fesselnden Cuts, Untertiteln, Musik und B-Roll inklusive"
    ],
    badge: "Starter Paket"
  },
  {
    name: "Reichweiten-Booster",
    description: "Ideal für Marken, die bereit sind, ihre Präsenz mit häufigerem, hochwertigem Content und tieferer strategischer Unterstützung zu skalieren.",
    features: [
      "2x Online-Strategiesitzungen pro Monat für kontinuierliche Abstimmung mit Marketing-Zielen",
      "2x Vor-Ort-Shootings pro Monat (je 6 Stunden)",
      "8 professionelle Videos pro Monat",
      "1 kostenlose Überarbeitung pro fertigem Video",
      "Premium-Schnitt mit filmischen Cuts, Untertiteln, Musik und B-Roll inklusive"
    ],
    badge: "Beliebteste Wahl"
  },
  {
    name: "Viral-Champion",
    description: "Entwickelt für Marken, die Social Media mit starker, täglicher Präsenz, kontinuierlichem Storytelling und maximaler Wirkung dominieren möchten.",
    features: [
      "4x Online-Strategiesitzungen pro Monat zur Verfeinerung der Content-Ausrichtung",
      "Bis zu 4x Vor-Ort-Shootings pro Monat für Hochvolumen-Produktion (je 5 Stunden)",
      "20 professionelle Videos pro Monat (5 pro Woche)",
      "1 kostenlose Überarbeitung pro fertigem Video",
      "Premium-Schnitt mit filmischen Cuts, Untertiteln, Musik und B-Roll inklusive"
    ],
    badge: "Premium Paket"
  }
];

const SocialMediaPackages = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [carouselApi, setCarouselApi] = useState<CarouselApi>();

  useEffect(() => {
    if (!carouselApi) return;

    carouselApi.on("select", () => {
      setCurrentSlide(carouselApi.selectedScrollSnap());
    });
  }, [carouselApi]);

  const PackageCard = ({ pkg, index }: { pkg: typeof packages[0], index: number }) => (
    <div
      className="relative rounded-2xl p-5 md:p-8 pt-10 md:pt-12 bg-card border-2 border-primary/20 hover:border-primary/40 shadow-lg hover:shadow-2xl hover:shadow-primary/10 transition-all duration-300 md:hover:scale-105 flex flex-col h-full min-h-[480px]"
    >
      {/* Island Badge */}
      <div className="absolute -top-[1px] left-1/2 -translate-x-1/2">
        <span className="bg-primary text-primary-foreground px-4 md:px-6 py-2 md:py-2.5 rounded-b-2xl text-xs md:text-sm font-semibold shadow-lg whitespace-nowrap border-2 border-t-0 border-primary">
          {pkg.badge}
        </span>
      </div>

      {/* Package Name */}
      <h3 className="text-xl md:text-2xl font-bold mb-3">{pkg.name}</h3>
      
      {/* Description */}
      <p className="text-xs md:text-sm text-muted-foreground mb-6 md:mb-8 min-h-[50px] md:min-h-[60px] leading-relaxed">
        {pkg.description}
      </p>

      {/* Features */}
      <ul className="space-y-3 md:space-y-4 mb-6 md:mb-8 flex-grow">
        {pkg.features.map((feature, featureIndex) => (
          <li key={featureIndex} className="flex items-start gap-2 md:gap-3">
            <div className="mt-0.5 p-1 rounded-full bg-primary/10 flex-shrink-0">
              <Check className="w-3 h-3 md:w-4 md:h-4 text-primary" />
            </div>
            <span className="text-xs md:text-sm text-muted-foreground leading-relaxed flex-1">
              {feature}
            </span>
          </li>
        ))}
      </ul>

      {/* CTA Button */}
      <Button
        variant="hero"
        size="lg"
        className="w-full text-sm md:text-base"
        asChild
      >
        <Link to="/termin">Paket anfragen</Link>
      </Button>
    </div>
  );

  return (
    <section id="packages" className="section-padding bg-gradient-to-b from-primary/5 to-background">
      <div className="container-custom">
        {/* Header */}
        <div className="text-center mb-16 space-y-4">
          <span className="text-sm uppercase tracking-wider text-primary font-semibold">
            Unsere Pakete
          </span>
          <h2 className="text-4xl md:text-5xl font-bold">
            Maßgeschneiderte{" "}
            <span className="text-gradient">Social Media Lösungen</span>
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Wähle das Paket, das zu deinen Zielen passt – von konsistentem Content bis zur vollständigen Social-Media-Dominanz.
          </p>
          <p className="text-sm text-muted-foreground max-w-2xl mx-auto mt-2">
            Alle Pakete mit mindestens 6 Monaten Laufzeit
          </p>
        </div>

        {/* Desktop Grid - Hidden on Mobile */}
        <div className="hidden md:grid md:grid-cols-3 gap-8 max-w-7xl mx-auto mt-8">
          {packages.map((pkg, index) => (
            <PackageCard key={index} pkg={pkg} index={index} />
          ))}
        </div>

        {/* Mobile Carousel - Hidden on Desktop */}
        <div className="md:hidden max-w-sm mx-auto mt-8">
          <Carousel
            setApi={setCarouselApi}
            opts={{
              align: "center",
              loop: true,
            }}
          >
            <CarouselContent>
              {packages.map((pkg, index) => (
                <CarouselItem key={index}>
                  <PackageCard pkg={pkg} index={index} />
                </CarouselItem>
              ))}
            </CarouselContent>
          </Carousel>

          {/* Dot Indicators */}
          <div className="flex justify-center gap-2 mt-6">
            {packages.map((_, index) => (
              <button
                key={index}
                className={`w-2 h-2 rounded-full transition-all duration-300 ${
                  currentSlide === index 
                    ? "bg-primary w-8" 
                    : "bg-primary/30"
                }`}
                onClick={() => {
                  carouselApi?.scrollTo(index);
                }}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="text-center mt-16">
          <p className="text-muted-foreground mb-4">
            Nicht sicher, welches Paket das Richtige ist?
          </p>
          <Button
            variant="hero-outline"
            size="lg"
            asChild
          >
            <Link to="/termin">Kostenlose Beratung buchen</Link>
          </Button>
        </div>
      </div>
    </section>
  );
};

export default SocialMediaPackages;
